import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { showToast } from '../lib/toast';
import { useConfirm } from '../hooks/useConfirm';
import DataTable from '../components/DataTable';
import TokenDisplay from '../components/TokenDisplay';
import clsx from 'clsx';
import { useAuth } from '../hooks/useAuth.jsx';

// Common US timezones for selection
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Mountain Time - Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
];

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { confirm, ConfirmDialog } = useConfirm();

  const [activeTab, setActiveTab] = useState(isAdmin ? 'users' : 'organization');
  const toISODate = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const addDays = (isoDate, deltaDays) => {
    const d = new Date(`${isoDate}T00:00:00`);
    d.setDate(d.getDate() + deltaDays);
    return toISODate(d);
  };

  const [trafficFilters, setTrafficFilters] = useState({
    domain: '',
    limit: 50,
    preset: '7', // '7' | '30' | '90' | 'custom'
    startDate: '',
    endDate: '',
  });
  const [newOrgName, setNewOrgName] = useState('');
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);

  // User management state
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('viewer');
  const [newUserName, setNewUserName] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [reclassifyingDomain, setReclassifyingDomain] = useState(null);

  // Campus management state
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [showAddCampus, setShowAddCampus] = useState(null); // org ID or null
  const [newCampusName, setNewCampusName] = useState('');
  const [copiedCampusToken, setCopiedCampusToken] = useState(null);

  // Timezone management state
  const [editingTimezone, setEditingTimezone] = useState(null); // org ID or null

  // Google Analytics state
  const [gaMeasurementId, setGaMeasurementId] = useState('');
  const [isEditingGA, setIsEditingGA] = useState(false);

  const queryClient = useQueryClient();

  const API_BASE = import.meta.env.VITE_API_URL || '/api';

  // Fetch site settings (public endpoint)
  const { data: settingsData } = useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE}/settings`);
        if (!res.ok) return { settings: {} };
        return res.json();
      } catch (e) {
        return { settings: {} };
      }
    },
    enabled: activeTab === 'general',
  });

  useEffect(() => {
    if (settingsData?.settings?.google_analytics_measurement_id) {
      setGaMeasurementId(settingsData.settings.google_analytics_measurement_id);
    }
  }, [settingsData]);

  const getSettingValue = (key, defaultValue) => {
    return settingsData?.settings?.[key] ?? defaultValue;
  };

  const updateRetentionMutation = useMutation({
    mutationFn: async ({ key, value }) => {
      const result = await api.patch('/settings', { key, value: String(value) });
      showToast.success('Retention setting updated');
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['site-settings']);
    },
    onError: (error) => {
      showToast.error(error.message || 'Failed to update setting');
    },
  });

  // Update GA setting mutation
  const updateGASettingMutation = useMutation({
    mutationFn: async (measurementId) => {
      try {
        const result = await api.patch('/settings', {
          key: 'google_analytics_measurement_id',
          value: measurementId || null,
        });
        showToast.success('Google Analytics setting updated');
        return result;
      } catch (error) {
        showToast.error(error.message || 'Failed to update setting');
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['site-settings']);
      setIsEditingGA(false);
    },
  });

  // Fetch users
  const { data: usersResponse, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      try {
        return await api.get('/users');
      } catch (e) {
        console.error('Failed to fetch users:', e);
        return { users: [] };
      }
    },
    enabled: isAdmin && activeTab === 'users',
  });

  const users = usersResponse?.users || [];

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (userData) => {
      try {
        const result = await api.post('/users', userData);
        showToast.success(`User ${userData.email} added successfully`);
        return result;
      } catch (error) {
        showToast.error(error.message || 'Failed to add user');
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setNewUserEmail('');
      setNewUserRole('viewer');
      setNewUserName('');
      setShowAddUser(false);
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({ id, ...data }) => {
      try {
        const result = await api.patch(`/users/${id}`, data);
        showToast.success('User updated successfully');
        return result;
      } catch (error) {
        showToast.error(error.message || 'Failed to update user');
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setEditingUser(null);
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async ({ id, email }) => {
      const confirmed = await confirm({
        title: 'Delete User',
        message: `Are you sure you want to remove ${email}? They will no longer be able to access the dashboard.`,
        confirmText: 'Delete User',
        confirmStyle: 'danger'
      });

      if (!confirmed) throw new Error('Cancelled');

      try {
        const result = await api.delete(`/users/${id}`);
        showToast.success(`User ${email} deleted successfully`);
        return result;
      } catch (error) {
        if (error.message !== 'Cancelled') {
          showToast.error(error.message || 'Failed to delete user');
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
    },
  });

  // Resend welcome email mutation
  const resendWelcomeEmailMutation = useMutation({
    mutationFn: async ({ id, email }) => {
      try {
        const result = await api.post(`/users/${id}/resend-welcome`);
        showToast.success(`Welcome email sent to ${email}`);
        return result;
      } catch (error) {
        if (error.response?.status === 429) {
          showToast.error(error.response.data.message || 'Please wait before resending');
        } else {
          showToast.error(error.response?.data?.error || 'Failed to send email');
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
    },
  });

  // Fetch organizations
  const { data: orgsResponse, isLoading: orgsLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      try {
        return await api.get('/organizations');
      } catch (e) {
        console.error('Failed to fetch organizations:', e);
        return { organizations: [] };
      }
    },
  });

  const organizations = orgsResponse?.organizations || [];

  // Create organization mutation
  const createOrgMutation = useMutation({
    mutationFn: async (name) => {
      try {
        const result = await api.post('/organizations', { name });
        showToast.success(`Organization "${name}" created successfully`);
        return result;
      } catch (error) {
        showToast.error(error.message || 'Failed to create organization');
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['organizations']);
      setNewOrgName('');
      setShowCreateOrg(false);
    },
  });

  // Regenerate token mutation
  const regenerateTokenMutation = useMutation({
    mutationFn: async (orgId) => {
      const confirmed = await confirm({
        title: 'Regenerate Token',
        message: 'This will invalidate the current token. All devices using the old token will stop reporting data. Are you sure?',
        confirmText: 'Regenerate',
        confirmStyle: 'danger'
      });

      if (!confirmed) throw new Error('Cancelled');

      try {
        const result = await api.post(`/organizations/${orgId}/regenerate-token`);
        showToast.success('Token regenerated successfully');
        return result;
      } catch (error) {
        if (error.message !== 'Cancelled') {
          showToast.error(error.message || 'Failed to regenerate token');
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['organizations']);
    },
  });

  // Toggle org active status
  const toggleOrgMutation = useMutation({
    mutationFn: async ({ id, isActive }) => {
      try {
        const result = await api.patch(`/organizations/${id}`, { isActive });
        showToast.success(`Organization ${isActive ? 'activated' : 'deactivated'} successfully`);
        return result;
      } catch (error) {
        showToast.error(error.message || 'Failed to update organization');
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['organizations']);
    },
  });

  // Update organization timezone
  const updateTimezoneMutation = useMutation({
    mutationFn: async ({ id, timezone }) => {
      try {
        const result = await api.patch(`/organizations/${id}`, { timezone });
        showToast.success('Timezone updated successfully');
        return result;
      } catch (error) {
        showToast.error(error.message || 'Failed to update timezone');
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['organizations']);
      setEditingTimezone(null);
    },
  });

  // Delete organization (must be deactivated first)
  const deleteOrgMutation = useMutation({
    mutationFn: async ({ id, name }) => {
      const confirmed = await confirm({
        title: 'Delete Organization',
        message: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
        confirmText: 'Delete Organization',
        confirmStyle: 'danger'
      });

      if (!confirmed) throw new Error('Cancelled');

      try {
        const result = await api.delete(`/organizations/${id}`);
        showToast.success(`Organization "${name}" deleted successfully`);
        return result;
      } catch (error) {
        if (error.message !== 'Cancelled') {
          showToast.error(error.message || 'Failed to delete organization');
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['organizations']);
      setExpandedOrg(null);
    },
  });

  // Fetch campuses for expanded organization
  const { data: campusesResponse, isLoading: campusesLoading } = useQuery({
    queryKey: ['campuses', expandedOrg],
    queryFn: async () => {
      try {
        return await api.get(`/campuses?orgId=${expandedOrg}`);
      } catch (e) {
        console.error('Failed to fetch campuses:', e);
        return { campuses: [] };
      }
    },
    enabled: !!expandedOrg,
  });

  const campuses = campusesResponse?.campuses || [];

  // Create campus mutation
  const createCampusMutation = useMutation({
    mutationFn: async ({ orgId, name }) => {
      try {
        const result = await api.post('/campuses', { orgId, name });
        showToast.success(`Campus "${name}" created successfully`);
        return result;
      } catch (error) {
        showToast.error(error.message || 'Failed to create campus');
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['campuses', expandedOrg]);
      setNewCampusName('');
      setShowAddCampus(null);
    },
  });

  // Toggle campus active status
  const toggleCampusMutation = useMutation({
    mutationFn: async ({ id, isActive }) => {
      try {
        const result = await api.patch(`/campuses/${id}`, { isActive });
        showToast.success(`Campus ${isActive ? 'activated' : 'deactivated'} successfully`);
        return result;
      } catch (error) {
        showToast.error(error.message || 'Failed to update campus');
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['campuses', expandedOrg]);
    },
  });

  // Regenerate campus token
  const regenerateCampusTokenMutation = useMutation({
    mutationFn: async ({ id, tokenType }) => {
      const confirmed = await confirm({
        title: 'Regenerate Campus Token',
        message: `This will invalidate the current ${tokenType} token. All ${tokenType} devices using the old token will stop reporting data. Are you sure?`,
        confirmText: 'Regenerate',
        confirmStyle: 'danger'
      });

      if (!confirmed) throw new Error('Cancelled');

      try {
        const result = await api.post(`/campuses/${id}/regenerate-token`, { tokenType });
        showToast.success(`${tokenType.charAt(0).toUpperCase() + tokenType.slice(1)} token regenerated successfully`);
        return result;
      } catch (error) {
        if (error.message !== 'Cancelled') {
          showToast.error(error.message || 'Failed to regenerate token');
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['campuses', expandedOrg]);
    },
  });

  // Delete campus
  const deleteCampusMutation = useMutation({
    mutationFn: async ({ id, name }) => {
      const confirmed = await confirm({
        title: 'Delete Campus',
        message: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
        confirmText: 'Delete Campus',
        confirmStyle: 'danger'
      });

      if (!confirmed) throw new Error('Cancelled');

      try {
        const result = await api.delete(`/campuses/${id}`);
        showToast.success(`Campus "${name}" deleted successfully`);
        return result;
      } catch (error) {
        if (error.message !== 'Cancelled') {
          showToast.error(error.message || 'Failed to delete campus');
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['campuses', expandedOrg]);
    },
  });

  const { data: trafficResponse, refetch: refetchTraffic } = useQuery({
    queryKey: ['trafficLog', trafficFilters],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (trafficFilters.domain) params.append('domain', trafficFilters.domain);
        params.append('limit', trafficFilters.limit);
        if (trafficFilters.preset === 'custom') {
          if (trafficFilters.startDate && trafficFilters.endDate) {
            params.append('startDate', trafficFilters.startDate);
            params.append('endDate', trafficFilters.endDate);
          } else {
            params.append('days', '7');
          }
        } else if (trafficFilters.preset) {
          params.append('days', trafficFilters.preset);
        }
        return await api.get(`/analytics/traffic-log?${params}`);
      } catch (e) {
        return { visits: [], total: 0 };
      }
    },
    enabled: isAdmin && activeTab === 'traffic',
  });

  const trafficLog = trafficResponse?.visits || [];

  const tabs = [
    ...(isAdmin ? [{ id: 'users', label: 'Users' }] : []),
    { id: 'organization', label: 'Organization' },
    ...(isAdmin ? [{ id: 'traffic', label: 'Traffic Log' }] : []),
    { id: 'general', label: 'General' },
  ];

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedToken(id);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  const copyCampusTokenToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCampusToken(id);
      setTimeout(() => setCopiedCampusToken(null), 2000);
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  const roleColors = {
    admin: 'bg-blue-50 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400',
    manager: 'bg-emerald-50 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400',
    viewer: 'bg-slate-100 dark:bg-slate-500/20 text-slate-600 dark:text-slate-400',
  };

  const roleLabels = {
    admin: 'Admin',
    manager: 'Manager',
    viewer: 'Viewer',
  };

  const trafficColumns = [
    {
      key: 'visited_at',
      label: 'Time',
      render: (value) => value ? new Date(value).toLocaleString() : '-',
    },
    {
      key: 'org_name',
      label: 'Organization',
      render: (value) => value || '-',
    },
    {
      key: 'domain',
      label: 'Domain',
      render: (value) => <span className="font-mono text-sm">{value}</span>,
    },
    {
      key: 'tool_name',
      label: 'Product',
      render: (value) => value || (
        <span className="text-slate-500 italic">Uncategorized</span>
      ),
    },
    {
      key: 'category_name',
      label: 'Category',
      render: (value) => value || '-',
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <button
          className="btn btn-secondary text-xs py-1 px-3"
          disabled={!isAdmin || reclassifyingDomain === row.domain}
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            try {
              setReclassifyingDomain(row.domain);
              await api.post('/analytics/reclassify-domain', { domain: row.domain });
              await refetchTraffic();
            } finally {
              setReclassifyingDomain(null);
            }
          }}
          title="Re-run tool matching for this domain"
        >
          {reclassifyingDomain === row.domain ? 'Reclassifying…' : 'Reclassify'}
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <ConfirmDialog />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold mb-2">Settings</h1>
          <p className="text-slate-400">Manage users, organizations, and system settings</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-surface-100 dark:bg-surface-800 rounded-lg w-fit border border-surface-200 dark:border-surface-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'bg-white dark:bg-surface-700 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* Info Card */}
          <div className="card p-4 bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/20">
            <p className="text-blue-700 dark:text-blue-300 text-sm">
              <strong>User Allowlist:</strong> Only users added here can sign in to the dashboard.
              Add team members by email and assign their role.
            </p>
          </div>

          {/* Add User Button */}
          {!showAddUser && (
            <button
              onClick={() => setShowAddUser(true)}
              className="btn btn-primary"
            >
              + Add User
            </button>
          )}

          {/* Add User Form */}
          {showAddUser && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4">Add New User</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    className="input"
                    placeholder="user@school.edu"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Display Name</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="John Smith"
                    value={newUserName}
                    onChange={(e) => setNewUserName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Role</label>
                  <select
                    className="input"
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value)}
                  >
                    <option value="viewer">Viewer - View analytics only</option>
                    <option value="manager">Manager - View analytics & manage products</option>
                    <option value="admin">Admin - Full access & manage users</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    createUserMutation.mutate({
                      email: newUserEmail,
                      displayName: newUserName || undefined,
                      role: newUserRole,
                    });
                  }}
                  className="btn btn-primary"
                  disabled={!newUserEmail || createUserMutation.isPending}
                >
                  {createUserMutation.isPending ? 'Adding...' : 'Add User'}
                </button>
                <button
                  onClick={() => {
                    setShowAddUser(false);
                    setNewUserEmail('');
                    setNewUserName('');
                    setNewUserRole('viewer');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
              {createUserMutation.isError && (
                <p className="text-red-400 text-sm mt-2">
                  {createUserMutation.error?.message || 'Failed to add user'}
                </p>
              )}
            </div>
          )}

          {/* Users List */}
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-surface-100 dark:bg-surface-800 border-b border-surface-200 dark:border-surface-700">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">User</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">Role</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">Last Login</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">Email Status</th>
                  <th className="text-right p-4 text-sm font-medium text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-700">
                {usersLoading && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-400">
                      Loading users...
                    </td>
                  </tr>
                )}
                {!usersLoading && users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-400">
                      No users yet. Add your first user above.
                    </td>
                  </tr>
                )}
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-surface-50 dark:hover:bg-surface-800/50">
                    <td className="p-4">
                      {editingUser?.id === user.id ? (
                        <input
                          type="text"
                          className="input text-sm"
                          value={editingUser.display_name || ''}
                          onChange={(e) => setEditingUser({ ...editingUser, display_name: e.target.value })}
                          placeholder="Display name"
                        />
                      ) : (
                        <div>
                          <p className="font-medium">{user.display_name}</p>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      {editingUser?.id === user.id ? (
                        <select
                          className="input text-sm"
                          value={editingUser.role}
                          onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                        >
                          <option value="viewer">Viewer</option>
                          <option value="manager">Manager</option>
                          <option value="admin">Admin</option>
                        </select>
                      ) : (
                        <span className={clsx('px-2 py-1 rounded text-xs font-medium', roleColors[user.role])}>
                          {roleLabels[user.role] || user.role}
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      {editingUser?.id === user.id ? (
                        <select
                          className="input text-sm"
                          value={editingUser.is_active ? 'active' : 'disabled'}
                          onChange={(e) => setEditingUser({ ...editingUser, is_active: e.target.value === 'active' })}
                        >
                          <option value="active">Active</option>
                          <option value="disabled">Disabled</option>
                        </select>
                      ) : (
                        <span className={clsx(
                          'px-2 py-1 rounded text-xs font-medium',
                          user.is_active ? 'bg-green-50 dark:bg-green-500/20 text-green-600 dark:text-green-400' : 'bg-red-50 dark:bg-red-500/20 text-red-600 dark:text-red-400'
                        )}>
                          {user.is_active ? 'Active' : 'Disabled'}
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-sm text-slate-400">
                      {user.last_login_at
                        ? new Date(user.last_login_at).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="p-4">
                      {user.welcome_email_status === 'sent' ? (
                        <div className="text-xs">
                          <span className="text-green-600 dark:text-green-400">✓ Sent</span>
                          {user.welcome_email_sent_at && (
                            <div className="text-slate-400 mt-1">
                              {new Date(user.welcome_email_sent_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      ) : user.welcome_email_status === 'failed' ? (
                        <span className="text-xs text-red-600 dark:text-red-400">✗ Failed</span>
                      ) : (
                        <span className="text-xs text-slate-400">Pending</span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      {editingUser?.id === user.id ? (
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => {
                              updateUserMutation.mutate({
                                id: user.id,
                                displayName: editingUser.display_name,
                                role: editingUser.role,
                                isActive: editingUser.is_active,
                              });
                            }}
                            className="btn btn-primary text-xs py-1 px-3"
                            disabled={updateUserMutation.isPending}
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingUser(null)}
                            className="btn btn-secondary text-xs py-1 px-3"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => resendWelcomeEmailMutation.mutate({ id: user.id, email: user.email })}
                            className="btn btn-secondary text-xs py-1 px-3"
                            disabled={resendWelcomeEmailMutation.isPending}
                            title="Resend welcome email"
                          >
                            Resend
                          </button>
                          <button
                            onClick={() => setEditingUser({ ...user })}
                            className="btn btn-secondary text-xs py-1 px-3"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteUserMutation.mutate({ id: user.id, email: user.email })}
                            className="btn btn-secondary text-xs py-1 px-3 text-red-400 hover:text-red-300"
                            disabled={deleteUserMutation.isPending}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Organization Tab */}
      {activeTab === 'organization' && (
        <div className="space-y-6">
          {/* Info Card */}
          <div className="card p-4 bg-cyan-50 dark:bg-cyan-500/10 border-cyan-200 dark:border-cyan-500/20">
            <p className="text-cyan-700 dark:text-cyan-300 text-sm">
              <strong>Organization Tokens</strong> are used to connect Chrome extensions to your EdFrame dashboard.
              Share the token with your IT team to deploy via Google Workspace managed policy, or enter manually in the extension.
            </p>
          </div>

          {/* Organizations List */}
          <div className="space-y-4">
            {orgsLoading && (
              <div className="card p-8 text-center text-slate-400">
                Loading organizations...
              </div>
            )}

            {!orgsLoading && organizations.length === 0 && (
              <div className="card p-8 text-center">
                <p className="text-slate-400 mb-4">No organizations yet. Create one to get started.</p>
                <button
                  onClick={() => setShowCreateOrg(true)}
                  className="btn btn-primary"
                >
                  Create Organization
                </button>
              </div>
            )}

            {organizations.map((org) => (
              <div key={org.id} className="card p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-slate-900 dark:text-white">
                      {org.name}
                      {!org.is_active && (
                        <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                          Inactive
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-slate-400">
                      Created {new Date(org.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setExpandedOrg(expandedOrg === org.id ? null : org.id)}
                      className="btn btn-secondary text-sm"
                    >
                      {expandedOrg === org.id ? 'Hide Campuses' : 'Manage Campuses'}
                    </button>
                    <button
                      onClick={() => toggleOrgMutation.mutate({ id: org.id, isActive: !org.is_active })}
                      className={clsx(
                        'btn text-sm',
                        org.is_active ? 'btn-secondary' : 'btn-primary'
                      )}
                      disabled={toggleOrgMutation.isPending}
                    >
                      {org.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    {!org.is_active && (
                      <button
                        onClick={() => {
                          if (confirm(`Permanently delete "${org.name}" and ALL associated data (campuses, devices, visits)?\n\nThis action cannot be undone.`)) {
                            deleteOrgMutation.mutate(org.id);
                          }
                        }}
                        className="btn btn-secondary text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        disabled={deleteOrgMutation.isPending}
                      >
                        {deleteOrgMutation.isPending ? 'Deleting...' : 'Delete'}
                      </button>
                    )}
                  </div>
                </div>

                {/* District-wide Token Display */}
                <div className="mb-4">
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">District-Wide Token (Legacy)</p>
                  <TokenDisplay
                    token={org.org_token}
                    label="Organization Token"
                    color="cyan"
                    onRegenerate={() => regenerateTokenMutation.mutate(org.id)}
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">Use this for district-wide tracking without campus/user-type segmentation</p>
                </div>

                {/* Timezone Settings */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                      Timezone
                    </p>
                    {editingTimezone !== org.id && (
                      <button
                        onClick={() => setEditingTimezone(org.id)}
                        className="text-xs text-cyan-500 hover:text-cyan-400"
                      >
                        Change
                      </button>
                    )}
                  </div>

                  {editingTimezone === org.id ? (
                    <div className="flex gap-2">
                      <select
                        className="input flex-1"
                        defaultValue={org.timezone || 'UTC'}
                        onChange={(e) => {
                          updateTimezoneMutation.mutate({
                            id: org.id,
                            timezone: e.target.value
                          });
                        }}
                      >
                        {TIMEZONES.map((tz) => (
                          <option key={tz.value} value={tz.value}>
                            {tz.label}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => setEditingTimezone(null)}
                        className="btn btn-secondary text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-700 dark:text-slate-300">
                      {TIMEZONES.find((tz) => tz.value === (org.timezone || 'UTC'))?.label || org.timezone || 'UTC'}
                    </div>
                  )}
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                    All time-based analytics (like the Peak Usage Times heatmap) will display in this timezone
                  </p>
                </div>

                {/* Campuses Section */}
                {expandedOrg === org.id && (
                  <div className="border-t border-surface-200 dark:border-surface-700 pt-4 mt-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-slate-800 dark:text-slate-200">Campuses</h4>
                      {!showAddCampus && (
                        <button
                          onClick={() => setShowAddCampus(org.id)}
                          className="btn btn-primary text-sm"
                        >
                          + Add Campus
                        </button>
                      )}
                    </div>

                    {/* Add Campus Form */}
                    {showAddCampus === org.id && (
                      <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 mb-4 border border-surface-200 dark:border-surface-700">
                        <div className="flex gap-4">
                          <input
                            type="text"
                            className="input flex-1"
                            placeholder="Campus name (e.g., Main High School)"
                            value={newCampusName}
                            onChange={(e) => setNewCampusName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && newCampusName.trim()) {
                                createCampusMutation.mutate({ orgId: org.id, name: newCampusName.trim() });
                              }
                            }}
                          />
                          <button
                            onClick={() => createCampusMutation.mutate({ orgId: org.id, name: newCampusName.trim() })}
                            className="btn btn-primary"
                            disabled={!newCampusName.trim() || createCampusMutation.isPending}
                          >
                            {createCampusMutation.isPending ? 'Creating...' : 'Create'}
                          </button>
                          <button
                            onClick={() => {
                              setShowAddCampus(null);
                              setNewCampusName('');
                            }}
                            className="btn btn-secondary"
                          >
                            Cancel
                          </button>
                        </div>
                        {createCampusMutation.isError && (
                          <p className="text-red-400 text-sm mt-2">
                            {createCampusMutation.error?.message || 'Failed to create campus'}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Campuses List */}
                    {campusesLoading && (
                      <p className="text-slate-400 text-sm">Loading campuses...</p>
                    )}

                    {!campusesLoading && campuses.length === 0 && (
                      <div className="bg-surface-100 dark:bg-surface-800/50 rounded-lg p-6 text-center border border-surface-200 dark:border-surface-700/50">
                        <p className="text-slate-400 text-sm mb-2">No campuses yet.</p>
                        <p className="text-slate-500 text-xs">Create campuses to generate separate staff and student tokens for granular tracking.</p>
                      </div>
                    )}

                    <div className="space-y-3">
                      {campuses.map((campus) => (
                        <div key={campus.id} className={clsx(
                          'bg-surface-100 dark:bg-surface-800/50 rounded-lg p-4 border border-surface-200 dark:border-surface-700/50',
                          !campus.is_active && 'opacity-60'
                        )}>
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h5 className="font-medium flex items-center gap-2 text-slate-900 dark:text-white">
                                {campus.name}
                                {!campus.is_active && (
                                  <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                                    Inactive
                                  </span>
                                )}
                              </h5>
                              <p className="text-xs text-slate-500">
                                {campus.staff_devices || 0} staff devices, {campus.student_devices || 0} student devices
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => toggleCampusMutation.mutate({ id: campus.id, isActive: !campus.is_active })}
                                className="btn btn-secondary text-xs py-1 px-3"
                                disabled={toggleCampusMutation.isPending}
                              >
                                {campus.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm(`Delete campus "${campus.name}"? All associated data will be removed.`)) {
                                    deleteCampusMutation.mutate({ id: campus.id, name: campus.name });
                                  }
                                }}
                                className="btn btn-secondary text-xs py-1 px-3 text-red-400 hover:text-red-300"
                                disabled={deleteCampusMutation.isPending}
                              >
                                Delete
                              </button>
                            </div>
                          </div>

                          {/* Staff and Student Tokens */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <TokenDisplay
                                token={campus.staff_token}
                                label="Staff Token"
                                color="emerald"
                                onRegenerate={() => regenerateCampusTokenMutation.mutate({ id: campus.id, tokenType: 'staff' })}
                              />
                            </div>
                            <div>
                              <TokenDisplay
                                token={campus.student_token}
                                label="Student Token"
                                color="purple"
                                onRegenerate={() => regenerateCampusTokenMutation.mutate({ id: campus.id, tokenType: 'student' })}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 text-center mt-4">
                  <div className="bg-surface-200 dark:bg-surface-800/50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">{org.device_count || 0}</p>
                    <p className="text-xs text-slate-500">Devices</p>
                  </div>
                  <div className="bg-surface-200 dark:bg-surface-800/50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">{org.total_visits || 0}</p>
                    <p className="text-xs text-slate-500">Total Visits</p>
                  </div>
                  <div className="bg-surface-200 dark:bg-surface-800/50 rounded-lg p-3">
                    <p className="text-2xl font-bold">
                      {org.is_active ? (
                        <span className="text-green-400">●</span>
                      ) : (
                        <span className="text-red-400">●</span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500">Status</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Create Organization Button */}
          {organizations.length > 0 && !showCreateOrg && (
            <button
              onClick={() => setShowCreateOrg(true)}
              className="btn btn-secondary"
            >
              + Add Organization
            </button>
          )}

          {/* Create Organization Form */}
          {showCreateOrg && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4">Create New Organization</h3>
              <div className="flex gap-4">
                <input
                  type="text"
                  className="input flex-1"
                  placeholder="Organization name (e.g., Springfield School District)"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newOrgName.trim()) {
                      createOrgMutation.mutate(newOrgName.trim());
                    }
                  }}
                />
                <button
                  onClick={() => createOrgMutation.mutate(newOrgName.trim())}
                  className="btn btn-primary"
                  disabled={!newOrgName.trim() || createOrgMutation.isPending}
                >
                  {createOrgMutation.isPending ? 'Creating...' : 'Create'}
                </button>
                <button
                  onClick={() => {
                    setShowCreateOrg(false);
                    setNewOrgName('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
              {createOrgMutation.isError && (
                <p className="text-red-400 text-sm mt-2">
                  {createOrgMutation.error?.message || 'Failed to create organization'}
                </p>
              )}
            </div>
          )}

          {/* Deployment Instructions */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4">Deployment Instructions</h3>
            <div className="space-y-4 text-sm text-slate-300">
              <div className="bg-surface-100 dark:bg-surface-800/50 border border-surface-200 dark:border-surface-700 rounded-lg p-4 mb-4">
                <p className="text-slate-700 dark:text-slate-300 text-sm">
                  <strong>Campus Tokens</strong> allow you to track usage per campus and by user type (staff vs student).
                  Deploy staff tokens to teacher devices and student tokens to student devices.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white mb-2">Option 1: Google Workspace Managed Policy (Recommended)</h4>
                <ol className="list-decimal list-inside space-y-1 text-slate-600 dark:text-slate-400">
                  <li>Go to Google Admin Console → Devices → Chrome → Apps & Extensions</li>
                  <li>Add the EdFrame extension by ID</li>
                  <li>Create separate OUs for Staff and Students at each campus</li>
                  <li>Under "Policy for extensions", add the appropriate token for each OU:</li>
                </ol>
                <pre className="mt-2 bg-slate-900 dark:bg-surface-800 rounded-lg p-3 overflow-x-auto">
                  <code className="text-cyan-400">{`// For Staff OU at a campus:
{
  "orgToken": {
    "Value": "CLNS-XXXXXX"  // Use campus STAFF token
  }
}

// For Students OU at the same campus:
{
  "orgToken": {
    "Value": "CLNS-YYYYYY"  // Use campus STUDENT token
  }
}`}</code>
                </pre>
              </div>
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white mb-2">Option 2: District-Wide Token (No Segmentation)</h4>
                <p className="text-slate-600 dark:text-slate-400 mb-2">
                  If you don't need per-campus or staff/student tracking, use the district-wide organization token for all devices.
                </p>
                <pre className="mt-2 bg-slate-900 dark:bg-surface-800 rounded-lg p-3 overflow-x-auto">
                  <code className="text-cyan-400">{`{
  "orgToken": {
    "Value": "${organizations[0]?.org_token || 'CLNS-XXXXXX'}"
  }
}`}</code>
                </pre>
              </div>
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white mb-2">Option 3: Manual Entry</h4>
                <p className="text-slate-600 dark:text-slate-400">
                  Users can enter any token manually by clicking the EdFrame extension icon and entering it in the popup.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Traffic Log Tab */}
      {activeTab === 'traffic' && (
        <div className="space-y-4">
          <div className="card p-4">
            <div className="flex gap-4 items-end">
              <div className="w-44">
                <label className="block text-sm font-medium mb-1">Date Range</label>
                <select
                  className="input"
                  value={trafficFilters.preset}
                  onChange={(e) => {
                    const next = e.target.value;
                    setTrafficFilters((f) => {
                      const nextState = { ...f, preset: next };
                      if (next === 'custom') {
                        const today = toISODate(new Date());
                        nextState.endDate = today;
                        nextState.startDate = addDays(today, -7);
                      }
                      return nextState;
                    });
                  }}
                >
                  <option value="7">Last 7 Days</option>
                  <option value="30">Last 30 Days</option>
                  <option value="90">Last 90 Days</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              {trafficFilters.preset === 'custom' && (
                <div className="flex gap-2 items-end">
                  <div>
                    <label className="block text-sm font-medium mb-1">Start</label>
                    <input
                      type="date"
                      className="input"
                      value={trafficFilters.startDate}
                      onChange={(e) => setTrafficFilters(f => ({ ...f, startDate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">End</label>
                    <input
                      type="date"
                      className="input"
                      value={trafficFilters.endDate}
                      onChange={(e) => setTrafficFilters(f => ({ ...f, endDate: e.target.value }))}
                    />
                  </div>
                </div>
              )}
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Filter by Domain</label>
                <input
                  type="text"
                  className="input"
                  placeholder="e.g., google.com"
                  value={trafficFilters.domain}
                  onChange={(e) => setTrafficFilters(f => ({ ...f, domain: e.target.value }))}
                />
              </div>
              <div className="w-32">
                <label className="block text-sm font-medium mb-1">Limit</label>
                <select
                  className="input"
                  value={trafficFilters.limit}
                  onChange={(e) => setTrafficFilters(f => ({ ...f, limit: parseInt(e.target.value) }))}
                >
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="250">250</option>
                  <option value="500">500</option>
                </select>
              </div>
              <button
                onClick={() => refetchTraffic()}
                className="btn btn-secondary"
              >
                Apply Filters
              </button>
            </div>
          </div>

          <div className="card p-4 bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/20">
            <p className="text-blue-700 dark:text-blue-300 text-sm">
              <strong>Traffic Log:</strong> Showing anonymous device visits. Total: {trafficResponse?.total || 0} visits.
            </p>
          </div>

          <DataTable
            columns={trafficColumns}
            data={trafficLog}
            emptyMessage="No traffic data available"
          />
        </div>
      )}

      {/* General Settings Tab */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          {/* Google Analytics */}
          {isAdmin && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4">Google Analytics</h3>
              <div>
                <label className="block text-sm font-medium mb-1">Measurement ID</label>
                {isEditingGA ? (
                  <div className="space-y-3">
                    <input
                      type="text"
                      className="input w-full max-w-sm"
                      placeholder="G-XXXXXXXXXX"
                      value={gaMeasurementId}
                      onChange={(e) => setGaMeasurementId(e.target.value.toUpperCase())}
                    />
                    <p className="text-sm text-slate-400">
                      Enter your Google Analytics 4 Measurement ID. Leave blank to disable tracking.
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => updateGASettingMutation.mutate(gaMeasurementId.trim())}
                        className="btn btn-primary"
                        disabled={updateGASettingMutation.isPending}
                      >
                        {updateGASettingMutation.isPending ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingGA(false);
                          setGaMeasurementId(settingsData?.settings?.google_analytics_measurement_id || '');
                        }}
                        className="btn btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      {settingsData?.settings?.google_analytics_measurement_id ? (
                        <span className="font-mono text-sm">
                          {settingsData.settings.google_analytics_measurement_id}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400 italic">Not configured</span>
                      )}
                    </div>
                    <button
                      onClick={() => setIsEditingGA(true)}
                      className="btn btn-secondary text-sm"
                    >
                      {settingsData?.settings?.google_analytics_measurement_id ? 'Edit' : 'Configure'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Data Retention */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4">Data Retention</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Visit Data Retention</p>
                  <p className="text-sm text-slate-400">How long to keep individual visit records</p>
                </div>
                <select
                  className="input w-40"
                  value={getSettingValue('visit_retention_days', '365')}
                  onChange={(e) => updateRetentionMutation.mutate({ key: 'visit_retention_days', value: e.target.value })}
                >
                  <option value="90">90 days</option>
                  <option value="180">180 days</option>
                  <option value="365">1 year</option>
                  <option value="730">2 years</option>
                </select>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Aggregate Data Retention</p>
                  <p className="text-sm text-slate-400">How long to keep daily aggregates</p>
                </div>
                <select
                  className="input w-40"
                  value={getSettingValue('aggregate_retention_days', '1825')}
                  onChange={(e) => updateRetentionMutation.mutate({ key: 'aggregate_retention_days', value: e.target.value })}
                >
                  <option value="365">1 year</option>
                  <option value="730">2 years</option>
                  <option value="1825">5 years</option>
                </select>
              </div>
            </div>
          </div>

          {/* Privacy Settings */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4">Privacy Settings</h3>
            <div className="space-y-4">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="font-medium">Anonymize User Reports</p>
                  <p className="text-sm text-slate-400">Hide individual user data from non-admin users</p>
                </div>
                <input type="checkbox" className="w-5 h-5 rounded bg-surface-700 border-surface-600 text-brand-500" defaultChecked />
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="font-medium">Auto-Block Sensitive Domains</p>
                  <p className="text-sm text-slate-400">Automatically filter healthcare, banking, etc.</p>
                </div>
                <input type="checkbox" className="w-5 h-5 rounded bg-surface-700 border-surface-600 text-brand-500" defaultChecked />
              </label>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button className="btn btn-primary">Save Settings</button>
          </div>
        </div>
      )}
    </div>
  );
}
